import { computed } from "vue";

export const required = (t: Function) => (value: any) =>
	(value !== null && value !== undefined && value !== "") ||
	t("validation.required");
export const largerThan = (t: Function, min: number) => (value: any) => {
	if (value === null || value === undefined || value === "") {
		return true;
	}
	const numericValue = Number(value);
	if (Number.isNaN(numericValue)) {
		return t("validation.largerThan", { min });
	}
	return numericValue > min || t("validation.largerThan", { min });
};
export const validTld = (t: Function) => (value: any) => {
	if (value === null || value === undefined || value === "") {
		return true; // let required() handle empties
	}

	if (typeof value !== "string") {
		return t("validation.invalidTld");
	}

	const trimmed = value.trim().toLowerCase();
	const tldRegex = /^[a-z0-9-]+\.[a-z]{2,}$/;

	return tldRegex.test(trimmed) || t("validation.invalidTld");
};
export const between =
	(t: Function, min: number, max: number) => (value: any) =>
		(value !== null && value !== undefined && value >= min && value <= max) ||
		t("validation.between", { min, max });
export const minLength = (t: Function, min: number) => (value: any) =>
	(value && value.length >= min) || t("validation.minLength", { min });
export const numericAndFloat = (t: Function) => (value: any) =>
	(!isNaN(value) && /^[+-]?([0-9]*[.])?[0-9]+$/.test(value)) ||
	t("validation.numericAndFloat");
export const maxLength = (t: Function, max: number) => (value: any) =>
	(value && value.length <= max) || t("validation.maxLength", { max });
export const email = (t: Function) => (value: any) =>
	(value && /\S+@\S+\.\S+/.test(value)) || t("validation.email");
export const validDomain = (t: Function) => (value) =>
	(value && /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/.test(value)) ||
	t("validation.domain");
export const endsWith =
	(t: Function, validSuffixes: string[]) => (value: any) =>
		(value && validSuffixes.some((suffix) => value.endsWith(suffix))) ||
		t("validation.endsWith", { suffixes: validSuffixes.join(", ") });

export const isTrustedDomain = (t: Function, domains: string[]) => {
	return (value: string) => {
		if (!value) return false;

		const isValid = domains.some((domain) =>
			value.toLowerCase().endsWith(domain.toLowerCase())
		);

		return (
			isValid ||
			t("validation.untrusted", {
				domains: domains.join(", "),
			})
		);
	};
};

export const phoneNumber =
	(t: Function, countryCodes: string[] = ["+31", "+49", "+44"]) =>
	(value: any) =>
		!value ||
		countryCodes.some((code) => new RegExp(`^\\${code}[0-9]+$`).test(value)) ||
		t("validation.phoneNumber");

// password validation rules

export const matchesPassword =
	(t: Function, matchString: string) => (value: any) =>
		value === matchString || t("validation.matchesPassword", { matchString });

export function usePasswordLength(password, minimalPasswordLength) {
	return computed(() => {
		const length = password.value.length;
		if (length >= minimalPasswordLength) {
			return 60;
		} else {
			return (length / minimalPasswordLength) * 60;
		}
	});
}

export function usePasswordRequirement() {
	const requirementPatterns = {
		uppercase_letter: /[A-Z]/,
		lowercase_letter: /[a-z]/,
		number: /[0-9]/,
		special_character: /[^A-Za-z0-9]/,
	};

	const checkPattern = (pattern, value) => pattern.test(value);

	return (requirement) => {
		return (password) => {
			return checkPattern(requirementPatterns[requirement], password);
		};
	};
}

export function getRequirementMessage() {
	return {
		uppercase_letter: "Must contain an uppercase letter",
		lowercase_letter: "Must contain a lowercase letter",
		number: "Must contain a number",
		special_character: "Must contain a special character",
	};
}

export function usePasswordLengthCheck(minimalPasswordLength) {
	return (password) => {
		return (
			password.length >= minimalPasswordLength ||
			`Password must be at least ${minimalPasswordLength} characters long.`
		);
	};
}

export const titleInputRules = (
	t: Function,
	overrides: { min?: number; max?: number } = {}
) => [
	required(t),
	minLength(t, overrides.min ?? 4),
	maxLength(t, overrides.max ?? 100),
];
